import type { Detail } from '@/api/orders/orders-types'
import type { Cart } from '@/api/production-carts/production-carts.types'

/**
 * Backend rule: a log added to a cart must have
 * ship_date <= cart.max_allowed_ship_date. Later dates are rejected.
 */
export const isCartBlockedByShipDate = (
  cart: Pick<Cart, 'max_allowed_ship_date'> | null | undefined,
  shipDate: string | null | undefined
): boolean => {
  if (!cart?.max_allowed_ship_date || !shipDate) return false
  return cart.max_allowed_ship_date < shipDate
}

/**
 * Carts items of the same order sit on right now (matched on `invoice`, since a details
 * list can span orders). Used to pin them to the top of the cart picker — orders are
 * grouped on a cart in the shop, and scrolling for that cart every time is what slows
 * the guys down.
 *
 * "Right now" is the item's first unfinished step: its cart, or — on rows where the cart
 * wasn't cascaded forward — the cart the last finished step put it on. An item with every
 * step done has left its carts, so it suggests none (the shop saw empty carts offered).
 */
export const getOrderAssignedCartIds = (
  detail: Pick<Detail, 'invoice'>,
  details: Pick<Detail, 'invoice' | 'production_logs'>[] | undefined
): number[] => {
  const ids: number[] = []
  for (const sibling of details ?? []) {
    if (sibling.invoice !== detail.invoice) continue
    const logs = sibling.production_logs ?? []
    const current = logs.findIndex((log) => !log?.end_time)
    if (current === -1) continue
    const cartId =
      logs[current]?.cart_id ??
      logs
        .slice(0, current)
        .reverse()
        .find((log) => log?.cart_id != null)?.cart_id
    if (cartId != null && !ids.includes(cartId)) ids.push(cartId)
  }
  return ids
}
